/**
 * NX-2738 (E12.6) — the tenant-facing "Data coverage" panel on project detail.
 *
 * Answers the question every customer eventually asks: "is this number low because we're
 * unhealthy, or because SeneSight hasn't seen everything yet?" Per mapped source it shows what IS
 * observed, what the connector could report but has not yet ("not yet configured" — the specific
 * gap, never a blended percentage), and honest not-connected states. The operational tier
 * (per-source sync times) renders only when the API projected it for this role (admin /
 * delivery-lead); a client-viewer simply sees the summary — absence, not a broken layout.
 *
 * Self-fetching sibling panel (same rationale as EstimationPanel): the payload is role-projected
 * server-side, so it cannot ride on the main project fetch without widening that contract.
 */
import { useApi } from "../api/useApi.js";
import { HelpTip, HonestState, Tag } from "../components.js";
import { helpFor } from "../metric-help.js";
import { formatDate } from "../format.js";

interface SourceSummary {
  readonly sourceType: string;
  readonly status: "contributing" | "no-data-yet" | "not-connected";
  readonly observed: readonly string[];
  readonly notObserved: readonly string[];
}
interface DataHealthPayload {
  readonly summary?: {
    readonly lastRefreshed: string | null;
    readonly sources: readonly SourceSummary[];
  };
  readonly operational?: {
    readonly sources: ReadonlyArray<{ sourceType: string; lastSyncedAt: string | null }>;
  };
}

/** Rendered names for source ids and entity types — display vocabulary, not metric labels. */
const SOURCE_NAME: Record<string, string> = {
  github: "GitHub",
  nexus: "Nexus",
  ado: "Azure DevOps",
  questionnaire: "Questionnaire",
};
const ENTITY_NAME: Record<string, string> = {
  commit: "commits",
  work_item: "work items",
  change: "changes",
  review: "reviews",
  check_run: "check runs",
  deployment: "deployments",
};
const sourceName = (id: string): string => SOURCE_NAME[id] ?? id;
const entityName = (id: string): string => ENTITY_NAME[id] ?? id.replaceAll("_", " ");

const STATUS_META: Record<SourceSummary["status"], { word: string; tone?: "green" | "amber" }> = {
  contributing: { word: "Contributing", tone: "green" },
  "no-data-yet": { word: "No data yet", tone: "amber" },
  "not-connected": { word: "Not connected" },
};

export function DataHealthPanel({ projectId }: { projectId: string }): React.ReactNode {
  const state = useApi<DataHealthPayload>(`/projects/${projectId}/data-health`);
  if (state.status !== "ready") return null; // loading, or a role the API refused: no wreckage
  const payload = state.envelope.data;
  const summary = payload.summary;
  if (summary === undefined) return null;
  const syncBySource = new Map(
    (payload.operational?.sources ?? []).map((s) => [s.sourceType, s.lastSyncedAt]),
  );
  const hasOperational = payload.operational !== undefined;
  return (
    <>
      <div className="sectitle">
        <h3 className="sec">Data coverage</h3>
        <span className="cnt">
          {summary.lastRefreshed === null
            ? "no data has landed yet"
            : `last refreshed ${formatDate(summary.lastRefreshed)}`}
          {" · unobserved is never counted as zero"}
        </span>
      </div>
      <div className={`grid cols-${Math.min(3, Math.max(1, summary.sources.length))} metrics`}>
        {summary.sources.length === 0 && (
          <HonestState
            kind="notobs"
            detail="No sources are mapped to this project yet — nothing is being observed."
          />
        )}
        {summary.sources.map((s) => {
          const meta = STATUS_META[s.status];
          if (s.status === "not-connected") {
            return (
              <HonestState
                key={s.sourceType}
                kind="notobs"
                title={sourceName(s.sourceType)}
                detail="Not connected — SeneSight has never synced this source for your tenant. Its signals are unobserved, not zero."
              />
            );
          }
          const help = helpFor("Data coverage");
          return (
            <div className="metric" key={s.sourceType}>
              <div className="lab">{sourceName(s.sourceType)}</div>
              <div className="v" style={{ fontSize: 18 }}>
                <Tag tone={meta.tone}>{meta.word}</Tag>
              </div>
              <div className="mc">
                {s.observed.length > 0 && (
                  <span>observing {s.observed.map(entityName).join(" · ")}</span>
                )}
                {s.observed.length === 0 && (
                  <span>synced, nothing observed for this project yet</span>
                )}
              </div>
              <div className="mc">
                <span>
                  {s.notObserved.length > 0
                    ? `not yet configured: ${s.notObserved.map(entityName).join(" · ")}`
                    : "observing everything this connector declares"}
                </span>
              </div>
              {hasOperational && (
                <div className="mc">
                  <span>
                    {syncBySource.get(s.sourceType)
                      ? `last synced ${formatDate(syncBySource.get(s.sourceType)!)}`
                      : "never synced"}
                  </span>
                </div>
              )}
              {help !== undefined && <HelpTip text={help} label="Data coverage" />}
            </div>
          );
        })}
      </div>
    </>
  );
}
